import styled from 'styled-components/macro'
import { SEARCH_BAR_FONT_SIZE, SEARCH_BAR_ICON_WIDTH } from './searchBarChrome'

const iconWidth = SEARCH_BAR_ICON_WIDTH
const clearButtonSize = '32px'
const clearButtonGap = '6px'

export const IconWrap = styled.div<{ hasValue?: boolean }>`
  color: ${({ hasValue }) =>
    hasValue ? 'var(--text-primary)' : 'var(--text-secondary)'};
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${iconWidth};
  fill: currentcolor;
  stroke: currentcolor;

  display: flex;
  align-items: center;
  justify-content: center;
`

export const Input = styled.input.attrs({
  type: 'search',
})<{ $hasClear?: boolean }>`
  background: inherit;
  border: none;
  color: inherit;
  cursor: inherit;
  font-family: inherit;
  font-size: ${SEARCH_BAR_FONT_SIZE};
  font-style: normal;
  height: 100%;
  letter-spacing: var(--letter-spacing-normal);
  outline: none;
  padding: 0
    ${({ $hasClear }) =>
      $hasClear ? `calc(${clearButtonSize} + ${clearButtonGap})` : '1rem'}
    0 ${iconWidth};
  width: 100%;
  z-index: 2;
  appearance: textfield;

  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration {
    -webkit-appearance: none;
    display: none;
  }

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    color: var(--text-secondary);
    font-weight: var(--font-weight-regular);
    font-style: normal;
  }
`

/**
 * H4 THEME NOTE: the clear chip and its glyph ride the semantic pair
 * (--surface-brand-soft ground, --text-muted-deep ink, --action-quiet-bg
 * hover) — the glyph in index.tsx inherits the button ink, so keep it free of
 * literal color props. What remains literal in this component is AnimatedLine's
 * BrandColors accents (react-spring tweens hex colors, not var() strings);
 * retiring those is folded into the SearchBar-retirement backlog
 * (docs/SHARED_FRONTEND_PROGRAM.md §3.0 "SearchBar (legacy) → FhSearchField
 * spec"), where FhSearchField (already themed) is the target.
 */
export const ClearButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${clearButtonSize};
  height: ${clearButtonSize};
  border: none;
  border-radius: 9px;
  /* de-islanded clear-button chip: lavender wash -> --surface-brand-soft, ink ->
     --text-muted-deep, hover -> --action-quiet-bg (§6 deltas) so it re-themes. */
  background: var(--surface-brand-soft);
  cursor: pointer;
  padding: 0;
  color: var(--text-muted-deep);

  &:hover {
    background: var(--action-quiet-bg);
  }
`

export const Wrap = styled.div<{
  disabled?: boolean
  hasFocus?: boolean
  isHovered?: boolean
}>`
  box-sizing: border-box;
  background: var(--surface-card);
  cursor: ${({ disabled, hasFocus }) =>
    disabled ? 'not-allowed' : hasFocus ? 'default' : 'pointer'};
  height: 3.2rem;
  overflow: hidden;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'default')};
  position: relative;
  /* Stacking context so the clear chip (z-index 3) stays inside the field.
     Without it, that z-index competes with a detail sidebar (z-index 2) and
     the chip paints on the panel when the sidebar overlays the list. */
  z-index: 0;
  font-weight: var(--font-weight-regular);
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  /* Hover wash de-islanded: --moss-wash is a Layer-1 mint primitive, so it
     stayed light under the dark themes' re-bound inks. Ruled onto --row-hover
     (the DS pointer-response surface, theme-bound + contrast-gated) rather
     than minting a pearl-identical pair: the mint was legacy BrandColors
     flavor, not FhSearchField-spec reference look (the successor has no hover
     wash at all), and brand flavor on hover stays with AnimatedLine's accent.
     NOT --surface-brand-soft — that is the clear chip's resting fill, which
     would vanish into the hovered capsule. */
  ${({ hasFocus, isHovered }) =>
    `
    background: ${
      hasFocus
        ? 'var(--surface-card)'
        : isHovered
        ? 'var(--row-hover)'
        : 'var(--surface-card)'
    };
  `}
`
